// The project's files, as a tree beside the editor: open a file by clicking
// it, make a program the one Run starts by double-clicking it, and rename,
// delete or run one from its menu. Files and folders dropped
// on the tree are added to the project. A picture can be dragged to Blip's
// panel, to show it to him (blip/panel.js).

// What a picture of the project dragged from the tree carries: its path.
export const PROJECT_FILE = "application/x-b3d-project-file";
const PICTURE = /\.(png|jpe?g|gif|bmp|webp)$/i;

// The files a drop holds, folders walked: [[path, File], ...].
export async function droppedFiles(dataTransfer) {
  const entries = [...dataTransfer.items].map((item) => item.webkitGetAsEntry && item.webkitGetAsEntry())
    .filter(Boolean);
  if (!entries.length) return [...dataTransfer.files].map((file) => [file.name, file]);
  const files = [];
  const walk = async (entry, prefix) => {
    if (entry.name.startsWith(".")) return;
    if (entry.isFile) {
      files.push([prefix + entry.name, await new Promise((resolve, reject) => entry.file(resolve, reject))]);
      return;
    }
    const reader = entry.createReader();
    for (;;) {
      const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const child of batch) await walk(child, prefix + entry.name + "/");
    }
  };
  for (const entry of entries) await walk(entry, "");
  return files;
}

// The files a folder input (<input webkitdirectory>) chose, without the
// folder's own name: [[path, File], ...], and that name.
export function chosenFolder(fileList) {
  const files = [...fileList];
  const folder = files.length ? files[0].webkitRelativePath.split("/")[0] : "";
  return {
    folder,
    files: files.map((file) => [file.webkitRelativePath.split("/").slice(1).join("/"), file])
      .filter(([path]) => path && !path.split("/").some((part) => part.startsWith("."))),
  };
}

export class FileTree {
  // actions: { open(path), rename(path, newPath), remove(path), setMain(path),
  //            add(files) }
  constructor(element, actions) {
    this.element = element;
    this.actions = actions;
    // Folders start closed; these are the ones opened.
    this.opened = new Set();
    this.project = null;
    this.selected = null;
    // Which files the people in a buddy session have open: path -> [{ colour, name }].
    this.presence = new Map();
    element.addEventListener("dragover", (event) => {
      // Its own pictures, on their way to Blip, do not drop here.
      if (event.dataTransfer.types.includes(PROJECT_FILE)) return;
      event.preventDefault();
      element.classList.add("dropping");
    });
    element.addEventListener("dragleave", (event) => {
      if (!element.contains(event.relatedTarget)) element.classList.remove("dropping");
    });
    element.addEventListener("drop", async (event) => {
      event.preventDefault();
      element.classList.remove("dropping");
      const files = await droppedFiles(event.dataTransfer);
      if (files.length) actions.add(files);
    });
    // A double click on a program makes it the one Run starts. Its second
    // click says so: a browser sends no dblclick when the row was drawn
    // again between the clicks.
    element.addEventListener("click", (event) => {
      if (event.detail !== 2 || event.target.closest("button, input")) return;
      const path = event.target.closest("li.file")?.dataset.path;
      if (path && /\.bb$/i.test(path) && this.project && path !== this.project.main) actions.setMain(path);
    });
  }

  show(project, selected = this.selected) {
    if (project !== this.project) this.opened.clear();
    this.project = project;
    this.selected = selected;
    this.reveal(selected);
    this.render();
  }

  // Opens the folders a file is in, to show it. Whether any were closed.
  reveal(path) {
    const parts = path ? path.split("/").slice(0, -1) : [];
    let changed = false;
    for (let i = 1; i <= parts.length; i++) {
      const folder = parts.slice(0, i).join("/");
      if (!this.opened.has(folder)) {
        this.opened.add(folder);
        changed = true;
      }
    }
    return changed;
  }

  // Moves the highlight without drawing the rows again.
  select(path) {
    this.selected = path;
    if (this.reveal(path)) return this.render();
    for (const row of this.element.querySelectorAll("li.file")) {
      const selected = row.dataset.path === path;
      row.classList.toggle("selected", selected);
      if (selected) row.setAttribute("aria-selected", "true");
      else row.removeAttribute("aria-selected");
    }
  }

  render() {
    const list = document.createElement("ul");
    list.className = "tree";
    list.setAttribute("role", "tree");
    const project = this.project;
    if (!project) return this.element.replaceChildren(list);
    // Folders first, then files, each by name.
    const root = { folders: new Map(), files: [] };
    for (const path of project.paths()) {
      const parts = path.split("/");
      let node = root;
      for (const part of parts.slice(0, -1)) {
        if (!node.folders.has(part)) node.folders.set(part, { folders: new Map(), files: [] });
        node = node.folders.get(part);
      }
      node.files.push(path);
    }
    const add = (node, prefix, depth) => {
      for (const [name, child] of [...node.folders].sort(([a], [b]) => a.localeCompare(b))) {
        const path = prefix + name;
        const open = this.opened.has(path);
        const row = this.row(depth, (open ? "▾ " : "▸ ") + name, "folder");
        row.setAttribute("aria-expanded", String(open));
        row.addEventListener("click", () => {
          open ? this.opened.delete(path) : this.opened.add(path);
          this.render();
        });
        list.append(row);
        if (open) add(child, path + "/", depth + 1);
      }
      for (const path of node.files.sort((a, b) => a.localeCompare(b))) list.append(this.fileRow(path, depth));
    };
    add(root, "", 0);
    if (!project.paths().length) {
      const empty = document.createElement("li");
      empty.className = "empty";
      empty.textContent = "No files yet. Add some, or drop them here.";
      list.append(empty);
    }
    this.element.replaceChildren(list);
    this.markPresence();
  }

  // Coloured dots beside the files the people in a buddy session have open
  // (buddies/panel.js).
  showPresence(presence) {
    this.presence = presence;
    this.markPresence();
  }

  markPresence() {
    for (const row of this.element.querySelectorAll("li.file")) {
      row.querySelector(".buddy-dots")?.remove();
      const people = this.presence.get(row.dataset.path);
      if (!people || !people.length) continue;
      const dots = document.createElement("span");
      dots.className = "buddy-dots";
      dots.title = `Open for ${people.map((person) => person.name).join(", ")}`;
      for (const person of people) {
        const dot = document.createElement("span");
        dot.className = "buddy-dot";
        dot.style.setProperty("--buddy", person.colour);
        dots.append(dot);
      }
      row.querySelector(".name").after(dots);
    }
  }

  row(depth, text, kind) {
    const row = document.createElement("li");
    row.className = kind;
    row.style.paddingLeft = `${8 + depth * 14}px`;
    row.setAttribute("role", "treeitem");
    const label = document.createElement("span");
    label.className = "name";
    label.textContent = text;
    row.append(label);
    return row;
  }

  fileRow(path, depth) {
    const name = path.split("/").pop();
    const row = this.row(depth, name, "file");
    row.dataset.path = path;
    row.title = path;
    if (path === this.selected) {
      row.classList.add("selected");
      row.setAttribute("aria-selected", "true");
    }
    if (path === this.project.main) {
      const badge = document.createElement("span");
      badge.className = "badge";
      badge.textContent = "runs";
      badge.title = "Run starts this file";
      row.append(badge);
    }
    row.addEventListener("click", (event) => {
      if (!event.target.closest("button, input")) this.actions.open(path);
    });
    if (PICTURE.test(path)) {
      row.draggable = true;
      row.addEventListener("dragstart", (event) => {
        event.dataTransfer.setData(PROJECT_FILE, path);
        event.dataTransfer.effectAllowed = "copy";
      });
    }
    const more = document.createElement("button");
    more.type = "button";
    more.className = "more";
    more.textContent = "⋯";
    more.title = `Actions for ${name}`;
    more.setAttribute("aria-label", more.title);
    more.addEventListener("click", (event) => {
      event.stopPropagation();
      this.menu(row, path, more);
    });
    row.append(more);
    return row;
  }

  menu(row, path, anchor) {
    document.querySelector(".tree-menu")?.remove();
    const menu = document.createElement("div");
    menu.className = "tree-menu";
    menu.setAttribute("role", "menu");
    const item = (label, action, disabled = false) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "menuitem");
      button.textContent = label;
      button.disabled = disabled;
      button.addEventListener("click", () => {
        menu.remove();
        action();
      });
      menu.append(button);
    };
    if (/\.world$/i.test(path) && this.actions.editWorld) item("Edit in World Editor", () => this.actions.editWorld(path),
      !!this.actions.worldEditorWaits?.());
    if (/\.bb$/i.test(path) && path !== this.project.main) item("Run this file", () => this.actions.setMain(path));
    item("Rename…", () => this.rename(row, path));
    item("Delete", () => {
      if (confirm(`Delete ${path}? This cannot be undone.`)) this.actions.remove(path);
    });
    const box = anchor.getBoundingClientRect();
    menu.style.left = `${box.left}px`;
    menu.style.top = `${box.bottom + 2}px`;
    document.body.append(menu);
    menu.querySelector("button").focus();
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "pointerdown" && menu.contains(event.target)) return;
      menu.remove();
      removeEventListener("pointerdown", close, true);
      removeEventListener("keydown", close, true);
    };
    addEventListener("pointerdown", close, true);
    addEventListener("keydown", close, true);
  }

  // Renames in place: Enter keeps the new name, Escape the old one.
  rename(row, path) {
    const label = row.querySelector(".name");
    const input = document.createElement("input");
    input.className = "rename";
    input.value = path;
    input.setAttribute("aria-label", `New path for ${path}`);
    label.replaceWith(input);
    input.focus();
    const dot = path.lastIndexOf(".");
    input.setSelectionRange(path.lastIndexOf("/") + 1, dot > path.lastIndexOf("/") ? dot : path.length);
    let done = false;
    const finish = (keep) => {
      if (done) return;
      done = true;
      const target = input.value.trim();
      if (keep && target && target !== path) this.actions.rename(path, target);
      else this.render();
    };
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") finish(true);
      if (event.key === "Escape") finish(false);
    });
    input.addEventListener("blur", () => finish(true));
  }
}
